import type { StatusTone } from '@shared/design-system/ui'
import type { LedgerEntry } from '../api/wallet.contract'

export type LedgerDirection = 'in' | 'held' | 'out' | 'void'

export function ledgerTypeLabel(type: LedgerEntry['type']): string {
  const labels: Record<LedgerEntry['type'], string> = {
    USER: 'Recebível',
    COMMISSION: 'Comissão',
    VENDEPAY: 'Taxa',
    RESERVE: 'Reserva',
  }
  return labels[type]
}

export function ledgerStatusLabel(status: LedgerEntry['status']): string {
  const labels: Record<LedgerEntry['status'], string> = {
    PENDING: 'A liberar',
    PROCESSING: 'Processando',
    PROCESSING_PARTIAL: 'Parcial',
    PAID: 'Pago',
    REVOKED: 'Revogado',
    DEBT_SETTLED: 'Abatido da dívida',
    DEBT_SETTLED_PARTIAL: 'Abatido (parcial)',
  }
  return labels[status]
}

export function ledgerStatusDescription(status: LedgerEntry['status']): string {
  const labels: Record<LedgerEntry['status'], string> = {
    PENDING: 'Ainda não liberado. Fica disponível na data de liberação.',
    PROCESSING: 'Repasse em processamento.',
    PROCESSING_PARTIAL: 'Parte do repasse em processamento.',
    PAID: 'Já liberado e disponível.',
    REVOKED: 'Cancelado: esta venda foi reembolsada ou sofreu chargeback.',
    DEBT_SETTLED: 'Este valor foi usado para abater uma dívida de reembolso/chargeback.',
    DEBT_SETTLED_PARTIAL:
      'Parte deste valor foi usada para abater a dívida; o restante continua a liberar.',
  }
  return labels[status]
}

export function ledgerStatusTone(status: LedgerEntry['status']): StatusTone {
  if (status === 'PAID') {
    return 'success'
  }
  if (status === 'PENDING' || status === 'PROCESSING' || status === 'PROCESSING_PARTIAL') {
    return 'awaiting'
  }
  if (status === 'REVOKED') {
    return 'closed'
  }
  return 'informative'
}

export function ledgerDirection(entry: Pick<LedgerEntry, 'type' | 'status'>): LedgerDirection {
  if (entry.status === 'REVOKED') {
    return 'void'
  }
  if (entry.status === 'DEBT_SETTLED' || entry.status === 'DEBT_SETTLED_PARTIAL') {
    return 'out'
  }
  if (entry.type === 'RESERVE') {
    return 'held'
  }
  return 'in'
}

export function ledgerDirectionLabel(direction: LedgerDirection): string {
  const labels: Record<LedgerDirection, string> = {
    in: 'Entrada',
    held: 'Retido (garantia)',
    out: 'Saída',
    void: 'Cancelado',
  }
  return labels[direction]
}

export function ledgerSignPrefix(direction: LedgerDirection): string {
  if (direction === 'in') {
    return '+'
  }
  if (direction === 'out') {
    return '−'
  }
  return ''
}

export function ledgerDateMeta(status: LedgerEntry['status']): {
  prefix: string
  withDate: boolean
} {
  if (status === 'PENDING' || status === 'PROCESSING' || status === 'PROCESSING_PARTIAL') {
    return { prefix: 'libera em', withDate: true }
  }
  if (status === 'PAID') {
    return { prefix: 'liberado em', withDate: true }
  }
  if (status === 'REVOKED') {
    return { prefix: 'reembolsado', withDate: false }
  }
  return { prefix: 'usado para abater dívida', withDate: false }
}

export function ledgerSourceLabel(source: LedgerEntry['source']): string {
  const labels: Record<LedgerEntry['source'], string> = {
    SALE: 'Venda',
    WITHDRAWAL_REMAINDER: 'Ajuste de saque',
    DEBT_REMAINDER: 'Sobra de reserva após reembolso',
    CURRENCY_CONVERSION: 'Conversão',
  }
  return labels[source]
}

const kindLabels: Record<string, string> = {
  PRINCIPAL: 'Produto principal',
  BUMP: 'Order bump',
  UPSELL: 'Upsell',
  DOWNSELL: 'Downsell',
}

const kindMetaLabels: Record<string, string> = {
  PRINCIPAL: 'produto principal',
  BUMP: 'order bump',
  UPSELL: 'upsell',
  DOWNSELL: 'downsell',
}

export function ledgerKindLabel(kind: string | undefined): string | undefined {
  if (!kind) {
    return undefined
  }
  return kindLabels[kind] ?? kind.toLowerCase()
}

export function ledgerKindMetaLabel(kind: string | undefined): string | undefined {
  if (!kind) {
    return undefined
  }
  return kindMetaLabels[kind] ?? kind.toLowerCase()
}

export function isSeparateFunnelSale(saleId: string): boolean {
  return saleId.startsWith('funnel:')
}

export function saleJourneyId(saleId: string): string {
  if (!isSeparateFunnelSale(saleId)) {
    return saleId
  }
  const originSaleId = saleId.split(':')[1]
  return originSaleId && originSaleId.length > 0 ? originSaleId : saleId
}

type SubscriptionInvoiceReference = {
  subscriptionId: string
  cycle: number
}

function subscriptionInvoiceReference(saleId: string): SubscriptionInvoiceReference | null {
  const prefix = 'invoice:'
  if (!saleId.startsWith(prefix)) {
    return null
  }
  const separator = saleId.lastIndexOf(':')
  if (separator <= prefix.length || separator === saleId.length - 1) {
    return null
  }
  const subscriptionId = saleId.slice(prefix.length, separator)
  const cycle = Number(saleId.slice(separator + 1))
  return Number.isInteger(cycle) && cycle > 0 ? { subscriptionId, cycle } : null
}

export function isSubscriptionInvoiceSale(saleId: string): boolean {
  return subscriptionInvoiceReference(saleId) !== null
}

export function saleReferenceLabel(saleId: string): string {
  const invoice = subscriptionInvoiceReference(saleId)
  return invoice !== null
    ? `Assinatura ${invoice.subscriptionId} · Renovação #${invoice.cycle}`
    : `Venda ${shortSaleId(saleId)}`
}

export function saleReferenceFullLabel(saleId: string): string {
  const invoice = subscriptionInvoiceReference(saleId)
  return invoice !== null
    ? `Assinatura ${invoice.subscriptionId} · Renovação #${invoice.cycle}`
    : `Venda ${saleId}`
}

export function saleReferenceMetaLabel(saleId: string): string {
  const invoice = subscriptionInvoiceReference(saleId)
  return invoice !== null
    ? `assinatura ${invoice.subscriptionId} · renovação #${invoice.cycle}`
    : `venda ${shortSaleId(saleId)}`
}

export function saleReferenceFullMetaLabel(saleId: string): string {
  const invoice = subscriptionInvoiceReference(saleId)
  return invoice !== null
    ? `assinatura ${invoice.subscriptionId} · renovação #${invoice.cycle}`
    : `venda ${saleId}`
}

export function saleViewActionLabel(saleId: string): string {
  return isSubscriptionInvoiceSale(saleId) ? 'Ver renovação' : 'Ver venda'
}

export function shortSaleId(saleId: string): string {
  return saleId.length > 12 ? `${saleId.slice(0, 8)}...` : saleId
}
